import { cn } from '@tebuto/lib/utils'
import type { WalletCheckoutPricing } from '@tebuto/payment/paypal/wallet-checkout-pricing'
import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'

declare global {
    interface Window {
        google?: {
            payments?: {
                api?: {
                    PaymentsClient: new (config: any) => any
                }
            }
        }
    }
}

interface GooglePayButtonProps {
    onCreateOrder: () => Promise<string>
    onApprove: (data: { orderID: string }) => Promise<void>
    onError: (error: any) => void
    disabled: boolean
    pricing: WalletCheckoutPricing
    onReady?: () => void
    useWidgetButton?: boolean
}

const WALLET_BUTTON_HEIGHT_PX = 45
const WALLET_BUTTON_BORDER_RADIUS_PX = 8
/** Google Pay "fill" mode scales label art to the element height; 40px matches native wallet button sizing. */
const GOOGLE_PAY_BUTTON_CONTENT_HEIGHT_PX = 40

export default function GooglePayButton({
    onCreateOrder,
    onApprove,
    onError,
    disabled,
    pricing,
    onReady,
    useWidgetButton = false
}: GooglePayButtonProps) {
    const buttonContainerRef = useRef<HTMLDivElement>(null)
    const [isScriptLoaded, setIsScriptLoaded] = useState(false)

    useEffect(() => {
        const loadGooglePayScript = () => {
            const scriptUrl = 'https://pay.google.com/gp/p/js/pay.js'

            if (document.querySelector(`script[src="${scriptUrl}"]`)) {
                setIsScriptLoaded(true)
                return
            }

            const script = document.createElement('script')
            script.src = scriptUrl
            script.async = true

            script.onload = () => {
                setIsScriptLoaded(true)
            }

            script.onerror = () => {
                console.error('Failed to load Google Pay script')
                onError(new Error('Failed to load Google Pay script'))
            }

            document.head.appendChild(script)
        }

        loadGooglePayScript()
    }, [onError])

    useEffect(() => {
        if (!isScriptLoaded) {
            return
        }

        const initializeGooglePay = async () => {
            try {
                const paypalGooglePayInstance = (window.paypal as any)?.Googlepay
                const googlePayApi = window.google?.payments?.api

                if (typeof window === 'undefined' || !googlePayApi || !paypalGooglePayInstance) {
                    setTimeout(initializeGooglePay, 100)
                    return
                }

                const googlePayConfig = await paypalGooglePayInstance().config()

                // Validate Google Pay configuration
                if (!googlePayConfig.merchantInfo || !googlePayConfig.allowedPaymentMethods) {
                    console.error('Google Pay configuration is incomplete:', googlePayConfig)
                    onError(new Error('Google Pay configuration is not properly set up'))
                    return
                }

                const isProduction =
                    typeof window !== 'undefined' &&
                    (window.location.hostname === 'tebuto.de' || window.location.hostname.endsWith('.tebuto.de'))

                const paymentsClient = new googlePayApi.PaymentsClient({
                    environment: isProduction ? 'PRODUCTION' : 'TEST',
                    paymentDataCallbacks: {
                        onPaymentAuthorized: async (paymentData: any) => {
                            try {
                                const orderId = await onCreateOrder()

                                const confirmResult = await paypalGooglePayInstance().confirmOrder({
                                    orderId,
                                    paymentMethodData: paymentData.paymentMethodData
                                })

                                if (confirmResult.status === 'APPROVED') {
                                    await onApprove({ orderID: orderId })
                                    return { transactionState: 'SUCCESS' }
                                }

                                return { transactionState: 'ERROR' }
                            } catch (error) {
                                console.error('Google Pay payment authorization error:', error)
                                return { transactionState: 'ERROR' }
                            }
                        }
                    }
                })

                const isReadyToPayRequest = {
                    apiVersion: 2,
                    apiVersionMinor: 0,
                    allowedPaymentMethods: googlePayConfig.allowedPaymentMethods
                }

                const response = await paymentsClient.isReadyToPay(isReadyToPayRequest)

                if (response.result && buttonContainerRef.current) {
                    if (buttonContainerRef.current.innerHTML) {
                        buttonContainerRef.current.innerHTML = ''
                    }

                    const button = paymentsClient.createButton({
                        buttonColor: 'white',
                        buttonType: 'book',
                        buttonLocale: 'de',
                        buttonSizeMode: 'fill',
                        buttonRadius: useWidgetButton ? WALLET_BUTTON_BORDER_RADIUS_PX : undefined,
                        allowedPaymentMethods: googlePayConfig.allowedPaymentMethods,
                        onClick: async () => {
                            if (disabled) {
                                console.log('Google Pay button is disabled')
                                return
                            }

                            try {
                                const grossPrice = Number(pricing.grossPrice)

                                if (Number.isNaN(grossPrice) || grossPrice <= 0) {
                                    throw new Error('Invalid price amount')
                                }

                                const taxRate = Number(pricing.taxRate)
                                const netPrice = grossPrice / (1 + taxRate / 100)
                                const vat = grossPrice - netPrice

                                const taxApplies = !pricing.isOutage && vat > 0

                                const formatPrice = (price: number) => price.toFixed(2)

                                const transactionInfo: any = {
                                    countryCode: 'DE',
                                    currencyCode: 'EUR',
                                    totalPriceStatus: 'FINAL',
                                    totalPrice: formatPrice(grossPrice),
                                    totalPriceLabel: 'Gesamt'
                                }

                                const displayItems = []

                                if (
                                    pricing.isOutage &&
                                    pricing.outageFeeApplies === 'yes' &&
                                    pricing.outageFeePrice &&
                                    Number(pricing.outageFeePrice) > 0
                                ) {
                                    displayItems.push({
                                        label: 'Ausfallgebühr',
                                        price: formatPrice(Number(pricing.outageFeePrice)),
                                        type: 'LINE_ITEM'
                                    })
                                } else {
                                    displayItems.push({
                                        label: `${pricing.label} am ${DateTime.fromISO(pricing.date).toFormat('dd.MM.yyyy')}`,
                                        price: taxApplies ? formatPrice(netPrice) : formatPrice(grossPrice),
                                        type: 'LINE_ITEM'
                                    })
                                }

                                if (taxApplies) {
                                    displayItems.push({
                                        label: `MwSt. (${pricing.taxRate}%)`,
                                        price: formatPrice(vat),
                                        type: 'TAX'
                                    })
                                }

                                if (displayItems.length > 0) {
                                    transactionInfo.displayItems = displayItems
                                }

                                const paymentDataRequest = {
                                    apiVersion: 2,
                                    apiVersionMinor: 0,
                                    transactionInfo,
                                    merchantInfo: googlePayConfig.merchantInfo,
                                    allowedPaymentMethods: googlePayConfig.allowedPaymentMethods,
                                    callbackIntents: ['PAYMENT_AUTHORIZATION']
                                }

                                await paymentsClient.loadPaymentData(paymentDataRequest)
                            } catch (error) {
                                if (
                                    (error as any).statusCode === 'CANCELED' ||
                                    (error as any).statusCode === 'DEVELOPER_ERROR' ||
                                    (error as any).statusCode === 'MERCHANT_ACCOUNT_ERROR'
                                ) {
                                    console.log('Google Pay payment cancelled or not available')
                                    return
                                }

                                console.error('Google Pay load payment data error:', error)
                                onError(error)
                            }
                        }
                    })

                    buttonContainerRef.current.appendChild(button)

                    const googlePayButtonHeightPx = useWidgetButton
                        ? GOOGLE_PAY_BUTTON_CONTENT_HEIGHT_PX
                        : WALLET_BUTTON_HEIGHT_PX

                    if (button instanceof HTMLElement) {
                        button.style.width = '100%'
                        button.style.height = `${googlePayButtonHeightPx}px`
                        button.style.minHeight = `${googlePayButtonHeightPx}px`
                        button.style.maxHeight = `${googlePayButtonHeightPx}px`
                        button.style.display = 'block'
                        button.style.margin = '0'
                        button.style.boxSizing = 'border-box'
                        if (useWidgetButton) {
                            button.classList.remove('border-inset')
                            button.style.border = 'none'
                            button.style.borderRadius = '0'
                            button.style.outline = 'none'
                            button.style.boxShadow = 'none'
                        }
                    }

                    const googlePayIframe = buttonContainerRef.current.querySelector('iframe')
                    if (googlePayIframe instanceof HTMLElement) {
                        googlePayIframe.style.height = `${googlePayButtonHeightPx}px`
                        googlePayIframe.style.minHeight = `${googlePayButtonHeightPx}px`
                        googlePayIframe.style.maxHeight = `${googlePayButtonHeightPx}px`
                    }

                    onReady?.()
                } else {
                    // Button is not eligible, but we still call onReady to not block loading
                    onReady?.()
                }
            } catch (error) {
                console.error('Google Pay initialization error:', error)
                onError(error)
                // Call onReady even on error to not block loading
                onReady?.()
            }
        }

        initializeGooglePay()
    }, [isScriptLoaded, onCreateOrder, onApprove, onError, disabled, pricing, onReady, useWidgetButton])

    return (
        <div
            ref={buttonContainerRef}
            className={cn(
                'w-full min-w-0',
                useWidgetButton ? 'widget-paypal-google-pay-slot' : 'min-h-[45px]',
                disabled ? 'pointer-events-none opacity-50' : ''
            )}
            style={
                useWidgetButton
                    ? {
                          height: `${WALLET_BUTTON_HEIGHT_PX}px`,
                          minHeight: `${WALLET_BUTTON_HEIGHT_PX}px`,
                          maxHeight: `${WALLET_BUTTON_HEIGHT_PX}px`
                      }
                    : undefined
            }
        />
    )
}
